import { useEffect, useState, useRef } from 'react';
import moment from 'moment';
import { AVATAR_IMAGE } from '../../assets/image';
import messageTextIcon from 'assets/img/message-text-square2.svg';
import HeadingWithIcon from 'pages/projects/kanban/shared/HeadingWithIcon';
import UserActivity from './UserActivity';
import AddComment from './AddComment';
import style from './Activity.module.css';
import CommentContainer from './CommentContainer';
import { useSocket } from '../../context/SocketContext';
import { useAuth } from 'contexts/AuthContext';
import APIServices from 'services/APIServices'; // Assuming this is your API service
import { selectLoader, ACTIVITY_PAGE_LIMIT } from 'utils/Helpers';
import Select from 'react-select';
import { fetchEnumOptions } from 'utils/fetchData';

const Activity = ({ milestoneId, defaultActivities, viewMode, projectBoardId, members, showSubTask, subtaskId }) => {
  const { user } = useAuth();
  const socket = useSocket();
  const scrollTimeout = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const [activities, setActivities] = useState(defaultActivities || []);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [statusOptions, setStatusOptions] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const scrollRef = useRef();

  // useEffect(() => {
  //   if (projectBoardId && (milestoneId || (showSubTask && subtaskId))) {
  //     setPage(1);
  //     setActivities([]);
  //     setHasMore(true);
  //     fetchActivities(1);
  //   }
  // }, [projectBoardId, milestoneId, subtaskId, showSubTask]);

  useEffect(() => {
    fetchStatusOptions();
  }, []);

  const fetchStatusOptions = async () => {
    try {
      const response = await fetchEnumOptions('kb_activity_filter');

      if (response?.length > 0) {
        const formattedStatus = response.map((option) => ({
          id: option._id,
          value: option.value,
          label: option.name
        }));

        setStatusOptions(formattedStatus);

        // ✅ DEFAULT SELECT FIRST OPTION
        const firstOption = formattedStatus[0];
        setSelectedStatus(firstOption);

        // // ✅ fetch activities with default filter
        // if (projectBoardId && (milestoneId || (showSubTask && subtaskId))) {
        //   setPage(1);
        //   setActivities([]);
        //   setHasMore(true);
        //   fetchActivities(1, firstOption);
        // }
      }
    } catch (error) {
      console.error('Error fetching status options:', error);
    }
  };

  // Fetch paginated activities
  const fetchActivities = async (pageNum = 1, statusOverride) => {
    if (!projectBoardId) return;

    setLoading(true);
    const activeStatus = statusOverride ?? selectedStatus;
    try {
      let url = `/project-activity?page=${pageNum}&limit=${ACTIVITY_PAGE_LIMIT}&projectBoardId=${projectBoardId}`;

      if (showSubTask && subtaskId) {
        url += `&subtaskId=${subtaskId}`;
      } else {
        url += `&milestoneId=${milestoneId}`;
      }

      if (activeStatus?.id) {
        url += `&type=${activeStatus.id}`;
      }

      const response = await APIServices.get(url);
      const newActivities = response.data || [];

      setActivities((prev) => (pageNum === 1 ? newActivities : [...prev, ...newActivities]));
      setHasMore(newActivities.length === ACTIVITY_PAGE_LIMIT);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to load activities:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = (option) => {
    setSelectedStatus(option || null);

    setPage(1);
    setActivities([]);
    setHasMore(true);

    // fetchActivities(1, option || null);
  };

  // Scroll Handler
  const handleScroll = () => {
    const el = scrollRef.current;

    // ✅ show scrollbar while scrolling
    setIsScrolling(true);
    clearTimeout(scrollTimeout.current);
    scrollTimeout.current = setTimeout(() => {
      setIsScrolling(false);
    }, 800);

    // ✅ your existing infinite scroll logic
    if (!el || loading || !hasMore) return;

    if (el.scrollHeight - el.scrollTop <= el.clientHeight + 5) {
      fetchActivities(page + 1);
    }
  };

  useEffect(() => {
    if (!socket) return;

    const handleActivities = (data) => {
      const newActivity = data?.activities?.[0];
      if (!newActivity) return;

      // scope check
      if (showSubTask) {
        if (data.subtaskId !== subtaskId) return;
      } else {
        if (data.milestoneId !== milestoneId) return;
      }

      const activityType = newActivity?.activityType;

      const isMentionedActivity =
        activityType === 'milestone_mentioned_comment' || activityType === 'subtask_mentioned_comment';

      const isNormalComment = activityType === 'milestone_comment' || activityType === 'subtask_comment';

      const isUserMentioned =
        Array.isArray(newActivity?.message_user_tags) &&
        newActivity.message_user_tags.some((tag) => tag.userId === user?._id);

      const filterValue = selectedStatus.value;
      console.log(filterValue, 'filterValue');

      let shouldRender = false;

      if (filterValue === 'all') {
        shouldRender = true;
      } else if (filterValue === 'comments') {
        shouldRender = (isNormalComment || isMentionedActivity) && !isUserMentioned;
      } else if (filterValue === 'mentioned') {
        shouldRender = isMentionedActivity && isUserMentioned;
      }

      if (!shouldRender) return;

      setActivities((prev) => {
        const exists = prev.some((a) => a._id === newActivity._id);
        if (exists) return prev;
        return [newActivity, ...prev];
      });
    };

    socket.on('activities', handleActivities);

    return () => {
      socket.off('activities', handleActivities);
    };
  }, [socket, milestoneId, subtaskId, showSubTask, selectedStatus, user?._id]);

  useEffect(() => {
    const currentRef = scrollRef.current;
    if (currentRef) {
      currentRef.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('scroll', handleScroll);
      }
    };
  }, [loading, page, hasMore]);

  // // Socket updates
  useEffect(() => {
    if (selectedStatus && projectBoardId && (milestoneId || (showSubTask && subtaskId))) {
      setPage(1);
      setActivities([]);
      setHasMore(true);
      fetchActivities(1, selectedStatus);
    }
  }, [projectBoardId, milestoneId, subtaskId, showSubTask, selectedStatus]);

  const handleAddComment = (value, messageUserTags = [], commentText) => {
    console.log(messageUserTags, 'messageUserTags');

    const payload = {
      message: value,
      activityType:
        messageUserTags.length > 0
          ? showSubTask
            ? 'subtask_mentioned_comment'
            : 'milestone_mentioned_comment'
          : showSubTask
            ? 'subtask_comment'
            : 'milestone_comment',
      createdBy: user?._id,
      activityTypeRef: 'ProjectActivity',
      message_user_tags: messageUserTags,
      projectBoardId,
      commentText
    };

    if (showSubTask) {
      payload.subtaskId = subtaskId;
      socket.emit('add-subtask-activity', payload, projectBoardId);
    } else {
      payload.milestoneId = milestoneId;
      socket.emit('add-activity', payload, projectBoardId);
    }
  };

  return (
    <div className={style.activity_wrapper}>
      <div style={{ marginBottom: '10px' }} className="activity-cover">
        <HeadingWithIcon icon={messageTextIcon} heading="Activity" />
        <div style={{ marginTop: 8 }} className="activity-dropdown">
          <Select
            options={statusOptions}
            value={selectedStatus}
            onChange={handleStatusChange}
            placeholder="Filter activity"
            isSearchable={false}
            // isClearable
          />
        </div>
      </div>
      <div className={style.message_wrapper}>
        {!viewMode && (
          <AddComment handleAddComment={handleAddComment} profilePic={user?.profilePic} members={members} />
        )}
        <div className={`${style.user_activities_wrapper} ${isScrolling ? style.scrollActive : ''}`} ref={scrollRef}>
          {activities?.length > 0 ? (
            activities.map((value) => {
              if (value?.activityType?.includes('activity')) {
                return (
                  <div className={style.comment_wrapper} key={value._id}>
                    <div className={style.comment_image}>
                      <img src={value.createdBy?.profilePic || AVATAR_IMAGE} alt="avatar" />
                    </div>
                    <UserActivity
                      name={user?._id === value.createdBy._id ? 'You' : value.createdBy?.fullName}
                      time={moment(value?.createdDate).fromNow()}
                      activity={value.message}
                      activityDetails={value}
                    />
                  </div>
                );
              } else if (value.activityType.includes('comment')) {
                return (
                  <CommentContainer
                    createdBy={value.createdBy}
                    createdDate={value.createdDate}
                    comment={value.message}
                    key={value._id}
                    milestoneId={milestoneId}
                    id={value._id}
                    projectBoardId={projectBoardId}
                    viewMode={viewMode}
                  />
                );
              }
              return null;
            })
          ) : (
            <p style={{ textAlign: 'center' }}>No activities yet.</p>
          )}
          {loading && selectLoader(30)}
        </div>
      </div>
    </div>
  );
};

export default Activity;
